import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import Input from "antd/es/input";
import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";
import { NumberField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import UpdateDealPayload from "@app/component/createEditEntity/EditStageModal/types/UpdateDealPayload";
import FormFields from "@app/component/FormFields";
import {
  CompanyRelationshipField,
  PersonRelationshipField,
} from "@app/component/FormFields/components/Associations";
import DealLossCommentField from "@app/component/FormFields/components/DealLossCommentField";
import DealLossReasonField from "@app/component/FormFields/components/DealLossReasonField";
import Divider from "@app/component/FormFields/components/Divider";
import FunnelStageField from "@app/component/FormFields/components/FunnelStageField";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import DatePicker from "@app/component/input/DatePicker";
import EntityModal from "@app/component/modal/EntityModal";
import StageTag from "@app/component/StageTag";
import DealClosedDateSettingValue from "@app/enum/DealClosedDateSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import AssociationsState from "@app/store/associations/AssociationsState";
import { getFunnelStages } from "@app/store/deal";
import { getOrganizationSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { findDifference } from "@app/util/arrayUtils";
import { MAX_DEAL_SCORE } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showClosingDateAlert from "@app/util/deal/showClosingDateAlert";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import dealToFormValues from "@app/util/form/dealToFormValues";
import EditDealFormValues from "@app/util/form/EditDealFormValues";
import { formatDate } from "@app/util/formatters";
import useCustomFieldValidation from "@app/util/hook/useCustomFieldValidation";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useSchema from "@app/util/hook/useSchema";
import getActualLayoutSchema from "@app/util/layout/getActualLayoutSchema";
import getMissingRequiredFields from "@app/util/layout/getMissingRequiredFields";
import { dealLayoutModel } from "@app/util/layout/impl";

import RelatedEntitiesMatching from "../../RelatedEntitiesMatching";
import { isEntityRelating, isEntityRelatingSuccessful } from "../CreateDealModal/store";
import { relateUnrelateEntities } from "../CreateDealModal/store/actions";

import File from "./components/File";
import styles from "./EditStageModal.module.scss";
import { changeAssociatedEntities, initializeEditStageModal, updateDeal } from "./store/actions";
import { getAssociationsState, isLoading } from "./store/selectors";

interface OwnProps {
  deal: Deal;
  fixedFunnelStage?: boolean;
  onHide: () => void;
  onUpdateDeal?: (payload: UpdateDealPayload) => void;
}

interface Props extends OwnProps {
  associations: AssociationsState;
  closedDateUpdateSetting: DealClosedDateSettingValue;
  entityRelatingSuccess: boolean;
  funnelStages: Record<Funnel["id"], Stage[]>;
  isEntityRelating: boolean;
  onChangeAssociatedEntities: typeof changeAssociatedEntities.request;
  onInitialize: typeof initializeEditStageModal;
  onSelfUpdateDeal: (payload: UpdateDealPayload) => void;
  relateUnrelateEntities: typeof relateUnrelateEntities.request;
  updating: boolean;
}

const EditStageModal: React.FC<Props> = ({
  associations,
  closedDateUpdateSetting,
  deal,
  entityRelatingSuccess,
  fixedFunnelStage,
  funnelStages,
  isEntityRelating,
  onChangeAssociatedEntities,
  onHide,
  onInitialize,
  onSelfUpdateDeal,
  onUpdateDeal,
  relateUnrelateEntities,
  updating,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Edit Stage", useAnalytics());
  useEffect(() => analytics.initiated(), [analytics]);

  useEffect(() => {
    onInitialize(deal);
  }, [deal, onInitialize]);

  const [form] = useForm<OnlyRequiredFields<EditDealFormValues, "funnelId">>();
  const [formRef, handleFinishFail] = useErroredFieldsScroller();

  const handleSaveClick = useCallback(() => form.submit(), [form]);
  const handleCancel = useCallback(() => {
    analytics.cancelled();
    onHide();
  }, [analytics, onHide]);

  const formLayout = useMemo(() => dealLayoutModel.getLayoutFor(deal), [deal]);
  const funnelId = useWatch("funnelId", form);
  const stageId = useWatch("stageId", form);
  const missingRequiredFieldsSet = useMemo(() => {
    if (!funnelId || !stageId) {
      return new Set();
    }
    const actualSchema = getActualLayoutSchema(formLayout, { funnelId, stageId });
    const schemaFields = getMissingRequiredFields(actualSchema, deal);
    return new Set(schemaFields.map(({ field }) => field));
  }, [deal, formLayout, funnelId, stageId]);

  const noAssociations = useMemo(
    () => !associations.associatedCompany && !associations.associatedPerson,
    [associations.associatedCompany, associations.associatedPerson]
  );

  const filterFields = useCallback(
    (schemaField: LayoutSchemaField) => missingRequiredFieldsSet.has(schemaField.field),
    [missingRequiredFieldsSet]
  );

  const handleCustomFieldValidate = useCustomFieldValidation(form, handleFinishFail);

  const handleFormFinish = useCallback(
    (formData) => {
      const [groupIdsToAdd, groupIdsToRemove] = formData.groups
        ? findDifference(
            (deal.groups ?? []).map(({ id }) => id),
            Array.from(formData.groups ?? [])
          )
        : [[], []];

      const dealStage = funnelStages[funnelId].find(({ id }) => id === formData.stageId)!;
      const today = new Date();

      const payload = {
        callback: () => {
          analytics.completed();
          onHide();
        },
        customFields: formData.customFields,
        customFieldsValueValidateCallback: handleCustomFieldValidate,
        deal: {
          ...deal,
          account: formData.companyId ? { id: formData.companyId } : undefined,
          amount: formData.amount ?? deal.amount,
          closingDate: formData.closingDate
            ? closedDateUpdateSetting === DealClosedDateSettingValue.CHANGE_TO_TODAY &&
              dealStage.type !== DealStageType.DEFAULT
              ? formatDate(today, "yyyy-MM-dd")
              : formatDate(formData.closingDate, "yyyy-MM-dd")
            : deal.closingDate,
          contact: formData.personId ? { id: formData.personId } : deal.contact,
          dealLossComment: formData.dealLossComment,
          dealLossReason: formData.dealLossReasonId ? { id: formData.dealLossReasonId } : undefined,
          funnel: { id: formData.funnelId },
          name: formData.name ?? deal.name,
          score: formData.score ?? deal.score,
          stage: dealStage,
          user: formData.user ?? deal.user,
        },
        groupIdsToAdd,
        groupIdsToRemove,
        layoutId: formLayout.id,
      };

      if (
        closedDateUpdateSetting === DealClosedDateSettingValue.ASK_EVERYTIME &&
        dealStage.type !== DealStageType.DEFAULT &&
        deal.stage.id !== dealStage.id &&
        deal.closingDate
      ) {
        showClosingDateAlert(
          () => {
            (onUpdateDeal ?? onSelfUpdateDeal)({
              ...payload,
              deal: { ...payload.deal, closingDate: formatDate(today, "yyyy-MM-dd") },
            });
          },
          () => {
            (onUpdateDeal ?? onSelfUpdateDeal)(payload);
          },
          intl
        );
      } else {
        (onUpdateDeal ?? onSelfUpdateDeal)(payload);
      }
    },
    [
      deal,
      funnelStages,
      funnelId,
      closedDateUpdateSetting,
      formLayout.id,
      analytics,
      onHide,
      intl,
      onUpdateDeal,
      onSelfUpdateDeal,
      handleCustomFieldValidate,
    ]
  );

  const [actualSchema] = useSchema({
    entityType: EntityType.DEAL,
    filterFields,
    layout: formLayout,
  });
  const showEntityRelateAlert = useMemo(() => {
    return (
      actualSchema.filter(({ field }) => {
        return "accountId" === field || "contactId" === field;
      }).length === 2
    );
  }, [actualSchema]);

  return (
    <>
      <EntityModal
        extra={
          showEntityRelateAlert ? (
            <RelatedEntitiesMatching
              associatedCompany={associations.associatedCompany}
              associatedPerson={associations.associatedPerson}
              entityRelatingSuccess={entityRelatingSuccess}
              isEntityRelating={isEntityRelating}
              relateUnrelateEntities={relateUnrelateEntities}
            />
          ) : null
        }
        maskClosable={false}
        okButtonProps={{
          disabled: updating || !formLayout,
          loading: updating,
        }}
        okText={intl.formatMessage({
          id: "editStageModel.footer.save",
          defaultMessage: "Save And Update Stage",
          description: "Change Stage modal - Apply button text",
        })}
        onCancel={handleCancel}
        onOk={handleSaveClick}
        open
        title={
          <div className={styles.title}>
            {intl.formatMessage(
              {
                id: "editStageModel.title",
                defaultMessage: "Change Stage{showStage, select, true { to {stage}} other {}}",
                description: "Change Stage To modal title",
              },
              {
                showStage: fixedFunnelStage,
                stage: <StageTag stage={deal.stage} />,
              }
            )}
          </div>
        }
        width="clamp(100vw, 100vw, 750px)"
      >
        <div className={styles.container} ref={formRef}>
          {formLayout ? (
            // TODO: think about extracting this EditDeal form into a separate component,
            // since the exact same form is used in Deal Preview pane's Details component.
            <Form
              form={form}
              initialValues={dealToFormValues(deal)}
              layout="vertical"
              onFinish={handleFormFinish}
              onFinishFailed={handleFinishFail}
              preserve
            >
              {fixedFunnelStage ? (
                <>
                  <Form.Item hidden name="funnelId">
                    <Input />
                  </Form.Item>
                  <Form.Item hidden name="stageId">
                    <Input />
                  </Form.Item>
                </>
              ) : (
                <>
                  <FunnelStageField autoSelectFunnel={false} required />
                  <Divider />
                </>
              )}
              {!missingRequiredFieldsSet.has("dealLossReasonId") && (
                // It is important to keep previous value for that field if field is not required
                <Form.Item hidden name={DealFieldName.DEAL_LOSS_REASON}>
                  <Input />
                </Form.Item>
              )}
              <FormFields
                disallowAdding
                entityType={EntityType.DEAL}
                fileComponent={File}
                filterFields={filterFields}
                layout={formLayout}
              >
                <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_REASON}>
                  <DealLossReasonField />
                </ConditionalFormField>

                <ConditionalFormField fieldName={DealFieldName.DEAL_LOSS_COMMENT}>
                  <DealLossCommentField />
                </ConditionalFormField>

                <ConditionalFormField fieldName={DealFieldName.SCORE}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <Form.Item
                      label={label}
                      name="score"
                      required={required}
                      rules={[{ required }]}
                    >
                      <NumberField
                        disabled={disabled}
                        inputClassName={styles.fullWidth}
                        max={MAX_DEAL_SCORE}
                        min={0}
                        placeholder={intl.formatMessage(
                          {
                            id: "editStageModel.field.score.placeholder",
                            defaultMessage: "0-{max}",
                            description: "Score field's placeholder on the Create Deal modal",
                          },
                          { max: MAX_DEAL_SCORE }
                        )}
                        precision={0}
                      />
                    </Form.Item>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={DealFieldName.CLOSING_DATE}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <Form.Item
                      label={label}
                      name="closingDate"
                      required={required}
                      rules={[{ required }]}
                    >
                      <DatePicker
                        allowClear
                        className={styles.fullWidth}
                        disabled={disabled}
                        format="PP"
                      />
                    </Form.Item>
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.COMPANY}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <CompanyRelationshipField
                      allowClear
                      associatedCompany={associations.associatedCompany}
                      associatedDeal={associations.associatedDeal}
                      associatedPerson={associations.associatedPerson}
                      disabled={disabled}
                      entityType={EntityType.DEAL}
                      label={label}
                      onChangeAssociatedEntities={onChangeAssociatedEntities}
                      relateEntities={relateUnrelateEntities}
                      required={required}
                      suggestedCompanies={
                        noAssociations ? undefined : associations.availableCompanies
                      }
                    />
                  )}
                </ConditionalFormField>

                <ConditionalFormField fieldName={ActivityFieldName.PERSON}>
                  {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                    <PersonRelationshipField
                      allowClear
                      associatedCompany={associations.associatedCompany}
                      associatedDeal={associations.associatedDeal}
                      associatedPerson={associations.associatedPerson}
                      disabled={disabled}
                      label={label}
                      onChangeAssociatedEntities={onChangeAssociatedEntities}
                      relateEntities={relateUnrelateEntities}
                      required={required}
                      suggestedPeople={noAssociations ? undefined : associations.availablePeople}
                    />
                  )}
                </ConditionalFormField>
              </FormFields>
            </Form>
          ) : (
            <FormNoLayoutAlert />
          )}
        </div>
      </EntityModal>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  associations: getAssociationsState(state),
  closedDateUpdateSetting: getOrganizationSettingValue(state)(
    OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE
  ),
  entityRelatingSuccess: isEntityRelatingSuccessful(state),
  funnelStages: getFunnelStages(state),
  isEntityRelating: isEntityRelating(state),
  updating: isLoading(state),
});

const mapDispatchToProps = {
  onChangeAssociatedEntities: changeAssociatedEntities.request,
  onInitialize: initializeEditStageModal,
  onSelfUpdateDeal: updateDeal.request,
  relateUnrelateEntities: relateUnrelateEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditStageModal);
